import { useCallback, useEffect, useState } from 'react';
import { adminApi } from '@/api/tmm/admin';
import type { RevenueReport, TenantView } from '@/api/tmm/types';
import type { Period } from '../dashboard/period';
import { TENANT_JOIN_PAGE_SIZE } from '../dashboard/useFleetDashboard';

export interface RevenueData {
  report: RevenueReport;
  tenantsBySlug: Map<string, TenantView>;
  /** When this load arrived — the client clock; the API publishes no as-of (TMMPDM-0035). */
  loadedAt: Date;
}

export interface UseRevenue {
  data: RevenueData | null;
  loading: boolean;
  error: boolean;
  /** The failure behind `error`, for `ErrorState`'s reference id. */
  failure: unknown;
  refetch: () => void;
}

export function useRevenue(period: Period, resellerId?: number): UseRevenue {
  const [reloadToken, setReloadToken] = useState(0);
  const [state, setState] = useState<{
    key: string;
    data: RevenueData | null;
    error: boolean;
    failure?: unknown;
  }>({
    key: '',
    data: null,
    error: false,
  });
  const key = `${period.from}:${period.to}:${resellerId ?? ''}:${reloadToken}`;

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      adminApi.revenue({
        from: period.from,
        to: period.to,
        ...(resellerId !== undefined ? { resellerId } : {}),
      }),
      adminApi.listTenants({ size: TENANT_JOIN_PAGE_SIZE }),
    ])
      .then(([report, list]) => {
        if (cancelled) return;
        const tenantsBySlug = new Map(list.tenants.map((t) => [t.slug, t]));
        setState({ key, data: { report, tenantsBySlug, loadedAt: new Date() }, error: false });
      })
      .catch((failure: unknown) => {
        if (!cancelled) setState({ key, data: null, error: true, failure });
      });
    return () => {
      cancelled = true;
    };
  }, [key, period.from, period.to, resellerId]);

  const refetch = useCallback(() => setReloadToken((n) => n + 1), []);
  const current = state.key === key;
  return {
    data: current ? state.data : null,
    loading: !current,
    error: current && state.error,
    failure: current ? state.failure : undefined,
    refetch,
  };
}
